import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import polylabel from 'polylabel';

import { useNvProtectedSitesWms } from '../../../../../pages/auctions/auctionProtectedSitesWms';
import SellTimberWmsIdentifyModal from './SellTimberWmsIdentifyModal';
import VBLeaflet from '@vb-js-commons/new/vbLeaflet';
import { swapCoords } from '@vb-js-commons/new/leafletConfig';

import {
  exitDocumentFullscreen,
  getFullscreenElement,
  requestElementFullscreen
} from '@utils/fullscreen';
import { Icon } from '@ui-component-library';

import { FellingClassesLegend } from '../../../helpers';

import { collectFeaturesFromMapSelection } from '../helpers/sellTimberMapSelection';
import { fellingApplicationKeyFromFeature } from '../helpers/sellTimberShared';
import {
  createSellTimberPlacedMarkerLeafletIcon,
  DEFAULT_SELL_TIMBER_MARKER_ICON,
  SELL_TIMBER_MARKER_NAME_LABEL_MIN_ZOOM,
  SELL_TIMBER_MARKER_PIN_HEIGHT_PX
} from '../helpers/sellTimberMarkerIcons';

/* global L */

/** Step 2: avdelnings-id / avverknings-id chips — independent of virkes name labels. */
const MIN_ZOOM_FOR_AREA_ID_LABELS = 13.5;

/**
 * Default min zoom for virkes name labels (read-only steg 3 & 4). Override with `options.mapLabelMinZoom`.
 * Lower = labels visible when more zoomed out.
 */
export const DEFAULT_VIRKES_MAP_LABEL_MIN_ZOOM = 13.5;
/** Steg 3 — slightly lower threshold than sammanfattning so etiketter syns tidigare. */
export const SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP3 = 12;
/** Steg 4 sammanfattning — strängare så kartan inte täcks vid utzoomning. */
export const SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP4 = 12;

const SECTION_OPACITY = 0.45;

/** User-chosen polygons/drawings; placed point uses `marker-tree.svg` like Karta */
const USER_CHOICE_POLYGON = {
  color: '#ffffff',
  weight: 4,
  opacity: 1,
  fillColor: '#dce75c',
  fillOpacity: 0.42
};

/**
 * Avverkningsanmälningar not yet chosen — purple so they stand out from sections and from
 * USER_CHOICE_POLYGON. Once selected, they use USER_CHOICE_POLYGON like other user picks.
 */
const FELLING_APPLICATION_UNSELECTED = {
  color: '#7e22ce',
  weight: 2,
  opacity: 0.85,
  fillColor: '#9333ea',
  fillOpacity: 0.22
};

function safeStopDraw(leafletRef) {
  const lf = leafletRef.current;
  if (lf?.state?.control) {
    lf.stopDraw();
  }
}

function addFellingApplicationLayers(
  geojson,
  group,
  selectedIds,
  onToggle,
  interactive,
  /** If set, skip features for which this returns false */
  includeFeatureKey = null
) {
  if (!geojson) return;
  const features =
    geojson.type === 'FeatureCollection'
      ? geojson.features
      : geojson.type === 'Feature'
        ? [geojson]
        : [];

  features.forEach((feature, index) => {
    const key = fellingApplicationKeyFromFeature(feature, index);
    if (includeFeatureKey && !includeFeatureKey(key)) return;
    const selected = selectedIds.includes(key);
    const layer = L.geoJSON(feature, {
      interactive,
      style: selected ? USER_CHOICE_POLYGON : FELLING_APPLICATION_UNSELECTED
    });
    layer.eachLayer((l) => {
      l.bringToFront();
      if (interactive) {
        l.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          onToggle?.(key);
        });
      }
      l.addTo(group);
    });
  });
}

function latLngCenterFromFeatureGeometry(geometry) {
  if (!geometry) return null;
  try {
    const gj = L.geoJSON({ type: 'Feature', geometry, properties: {} });
    const b = gj.getBounds();
    return b?.isValid?.() ? b.getCenter() : null;
  } catch {
    return null;
  }
}

/** Shoelace area of one ring in [lng, lat] space (relative size only; for MultiPolygon part pick). */
function ringAreaLngLat(ring) {
  if (!ring || ring.length < 3) return 0;
  let sum = 0;
  const n = ring.length;
  const closed =
    ring[0][0] === ring[n - 1][0] && ring[0][1] === ring[n - 1][1] ? n - 1 : n;
  for (let i = 0; i < closed; i++) {
    const j = (i + 1) % closed;
    sum += ring[i][0] * ring[j][1];
    sum -= ring[j][0] * ring[i][1];
  }
  return Math.abs(sum / 2);
}

/**
 * Interior label anchor (pole of inaccessibility) — better for irregular polygons than bbox center.
 * @see https://github.com/mapbox/polylabel
 */
function latLngLabelAnchorFromGeometry(geometry) {
  if (!geometry?.type) return null;
  /** GeoJSON [lng, lat]; precision in the same units (degrees). */
  const precision = 1e-6;
  try {
    if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
      const [lng, lat] = geometry.coordinates;
      return L.latLng(lat, lng);
    }
    if (geometry.type === 'Polygon' && geometry.coordinates?.length) {
      const c = polylabel(geometry.coordinates, precision);
      return L.latLng(c[1], c[0]);
    }
    if (geometry.type === 'MultiPolygon' && geometry.coordinates?.length) {
      let best = null;
      let bestArea = -1;
      for (const poly of geometry.coordinates) {
        if (!poly?.[0]?.length) continue;
        const area = ringAreaLngLat(poly[0]);
        const c = polylabel(poly, precision);
        if (area >= bestArea) {
          bestArea = area;
          best = c;
        }
      }
      if (best) return L.latLng(best[1], best[0]);
    }
  } catch {
    // fallback below
  }
  return latLngCenterFromFeatureGeometry(geometry);
}

function escapeHtmlForLeafletLabel(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Leaflet applies `iconSize` as width/height on the icon node — [0,0] collapses layout to one column of glyphs. */
const VIRKES_LABEL_ICON_W = 260;
/** Room for several wrapped lines without clipping (Leaflet sizes the icon box from iconSize). */
const VIRKES_LABEL_ICON_H = 200;

let sellTimberLeafletDivIconCssInjected = false;
function ensureSellTimberLeafletDivIconCss() {
  if (sellTimberLeafletDivIconCssInjected || typeof document === 'undefined') return;
  sellTimberLeafletDivIconCssInjected = true;
  const el = document.createElement('style');
  el.setAttribute('data-sell-timber-map-div-icon-labels', '1');
  el.textContent =
    '.leaflet-div-icon.sell-timber-virkes-map-label-icon,.leaflet-div-icon.sell-timber-step2-area-id-label-icon{background:transparent!important;border:none!important;}';
  document.head.appendChild(el);
}

/** Short single-line id chips for step 2 (avdelning localID / avverknings-id). */
const STEP2_AREA_LABEL_W = 168;
const STEP2_AREA_LABEL_H = 36;
const MARKER_NAME_LABEL_GAP_PX = 4;

/** White chip label — shared by polygon ids and placed marker names. */
function sellTimberMapChipLabelDivIcon(text, iconAnchorY) {
  ensureSellTimberLeafletDivIconCss();
  const safe = escapeHtmlForLeafletLabel(text);
  return L.divIcon({
    className: 'sell-timber-step2-area-id-label-icon',
    html: `<div style="width:${STEP2_AREA_LABEL_W}px;height:${STEP2_AREA_LABEL_H}px;display:flex;align-items:center;justify-content:center;box-sizing:border-box;background:transparent!important;border:none!important;pointer-events:none;overflow:visible;">
  <div style="box-sizing:border-box;max-width:${STEP2_AREA_LABEL_W - 4}px;padding:2px 7px;background:rgba(255,255,255,0.93);border:1px solid #b8b8b8;border-radius:4px;font:11px/1.2 system-ui,-apple-system,sans-serif;color:#1a1a1a;box-shadow:0 1px 3px rgba(0,0,0,0.12);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:center;font-weight:600;">${safe}</div>
</div>`,
    iconSize: [STEP2_AREA_LABEL_W, STEP2_AREA_LABEL_H],
    iconAnchor: [Math.round(STEP2_AREA_LABEL_W / 2), iconAnchorY]
  });
}

function step2AreaIdLabelDivIcon(text) {
  return sellTimberMapChipLabelDivIcon(text, Math.round(STEP2_AREA_LABEL_H / 2));
}

/** Name chip above pin tip — same box style as avdelning labels. */
function placedMarkerNameLabelDivIcon(text) {
  return sellTimberMapChipLabelDivIcon(
    text,
    STEP2_AREA_LABEL_H + SELL_TIMBER_MARKER_PIN_HEIGHT_PX + MARKER_NAME_LABEL_GAP_PX
  );
}

function virkesSummaryLabelDivIcon(text) {
  ensureSellTimberLeafletDivIconCss();
  const safe = escapeHtmlForLeafletLabel(text);
  return L.divIcon({
    className: 'sell-timber-virkes-map-label-icon',
    html: `<div style="width:${VIRKES_LABEL_ICON_W}px;height:${VIRKES_LABEL_ICON_H}px;display:flex;align-items:center;justify-content:center;box-sizing:border-box;background:transparent!important;border:none!important;pointer-events:none;overflow:visible;">
  <div style="box-sizing:border-box;max-width:min(240px,42vw);width:max-content;min-width:0;padding:4px 8px;background:rgba(255,255,255,0.94);border:1px solid #c8c8c8;border-radius:6px;font:12px/1.3 system-ui,-apple-system,sans-serif;color:#1a1a1a;box-shadow:0 1px 4px rgba(0,0,0,0.12);white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;text-align:center;">${safe}</div>
</div>`,
    iconSize: [VIRKES_LABEL_ICON_W, VIRKES_LABEL_ICON_H],
    iconAnchor: [Math.round(VIRKES_LABEL_ICON_W / 2), Math.round(VIRKES_LABEL_ICON_H / 2)]
  });
}

function addBorderLayers(borderCoords, targetGroup) {
  if (!borderCoords?.length) return;
  borderCoords.forEach((ring) => {
    const layer = L.polygon(swapCoords(ring), {
      color: '#FFFFFF',
      fillOpacity: 0,
      interactive: false,
      weight: 2
    });
    layer.addTo(targetGroup);
  });
}

/**
 * @param {{
 *   selection: {
 *     mapMode: string,
 *     sections: unknown[],
 *     borderCoords: unknown,
 *     fellingApplicationsGeojson: unknown,
 *     selectedSectionLocalIds: string[],
 *     selectedFellingApplicationIds: string[],
 *     drawnPolygons: unknown[],
 *     placedMarkers?: unknown[],
 *     markerMoveTargetId?: string | null,
 *     customAreaFromAd?: unknown
 *   },
 *   handlers?: {
 *     onToggleSection?: (localKey: string) => void,
 *     onToggleFellingApplication?: (id: string) => void,
 *     onDrawComplete?: (feature: unknown) => void,
 *     onMarkerAddAt?: (latlng: { lng: number, lat: number }) => void,
 *     onMarkerSelectForMove?: (markerId: string) => void,
 *     onMarkerMoveTo?: (markerId: string, latlng: { lng: number, lat: number }) => void,
 *     onRemoveDrawnPolygon?: (index: number) => void,
 *     onRemoveMarker?: (markerId: string) => void,
 *     onRemoveHydratedAd?: () => void
 *   },
 *   options?: {
 *     removeMode?: boolean,
 *     readOnly?: boolean,
 *     mapId?: string,
 *     embedHeight?: string,
 *     onlySelectedOverlays?: boolean,
 *     hydratedAdGeometry?: unknown,
 *     virkesLabels?: string[] — steg 3 & 4: names aligned with {@link collectFeaturesFromMapSelection} order
 *     mapLabelMinZoom?: number — min zoom for virkes labels only (steg 3 vs 4 use different defaults)
 *     showAreaIdLabels?: boolean — step 2: show avdelning `localID` and avverknings-id on visible polygons
 *   }
 * }} props
 */
export default function SellTimberMap({ selection, handlers = {}, options = {} }) {
  const {
    mapMode,
    sections,
    borderCoords,
    fellingApplicationsGeojson,
    selectedSectionLocalIds,
    selectedFellingApplicationIds,
    drawnPolygons,
    placedMarkers: placedMarkersProp,
    markerMoveTargetId,
    customAreaFromAd
  } = selection;

  const placedMarkers = useMemo(
    () =>
      Array.isArray(placedMarkersProp) && placedMarkersProp.length
        ? placedMarkersProp
        : [],
    [placedMarkersProp]
  );

  const {
    onToggleSection,
    onToggleFellingApplication,
    onDrawComplete,
    onMarkerAddAt,
    onMarkerSelectForMove,
    onMarkerMoveTo,
    onRemoveDrawnPolygon,
    onRemoveMarker,
    onRemoveHydratedAd
  } = handlers;

  const {
    removeMode = false,
    readOnly = false,
    mapId = 'sell_timber_vb_leaflet',
    embedHeight = 'min(70vh, 560px)',
    onlySelectedOverlays = false,
    hydratedAdGeometry = null,
    virkesLabels = null,
    showAreaIdLabels = false,
    mapLabelMinZoom,
    markerEmptyHint = null,
    markerHasMarkersHint = null
  } = options;

  const virkesLabelMinZoom =
    mapLabelMinZoom != null && Number.isFinite(Number(mapLabelMinZoom))
      ? Number(mapLabelMinZoom)
      : DEFAULT_VIRKES_MAP_LABEL_MIN_ZOOM;
  const leafletRef = useRef(null);
  const mapShellRef = useRef(null);
  const [mapModalContainer, setMapModalContainer] = useState(null);
  const [wmsIdentifyModal, setWmsIdentifyModal] = useState({ open: false, loading: false, sections: [] });
  const overlaysRef = useRef(null);
  const virkesLabelsGroupRef = useRef(null);
  const areaIdLabelsGroupRef = useRef(null);
  const mapReadyRef = useRef(false);
  const [mapShellFullscreen, setMapShellFullscreen] = useState(false);
  /** Only fit bounds once when overlays first have valid bounds — avoids jumping on mode toggles / draw. */
  const hasDoneInitialFitRef = useRef(false);
  /** Bumps when the Leaflet featureGroup is attached so the draw effect re-runs (map init is async vs first sections paint). */
  const [overlayEpoch, setOverlayEpoch] = useState(0);
  /** Bumps on `zoomend` so label layers re-evaluate against zoom thresholds. */
  const [mapZoomForLabels, setMapZoomForLabels] = useState(null);

  const onWmsIdentifyStart = () => {
    setWmsIdentifyModal({ open: true, loading: true, sections: [] });
  };

  const onWmsIdentifyComplete = (sections) => {
    setWmsIdentifyModal((prev) => ({ ...prev, loading: false, sections: sections || [] }));
  };

  const closeWmsIdentifyModal = () => {
    setWmsIdentifyModal({ open: false, loading: false, sections: [] });
  };

  /** WMS GetFeatureInfo shares the map `click` event — skip while drawing, placing a marker, or remove mode. */
  const shouldRunWmsIdentify = () => {
    if (removeMode) return false;
    if (mapMode === 'draw' || mapMode === 'marker') return false;
    return true;
  };

  useNvProtectedSitesWms(leafletRef, {
    onIdentifyStart: onWmsIdentifyStart,
    onIdentifyComplete: onWmsIdentifyComplete,
    shouldRunIdentify: shouldRunWmsIdentify
  });

  const finishDraw = useCallback(
    (e) => {
      if (mapMode !== 'draw') return;
      const gj = e.layer?.toGeoJSON?.();
      if (gj && onDrawComplete) {
        onDrawComplete(gj);
      }
      const map = leafletRef.current?.state?.map;
      const layer = e.layer;
      window.setTimeout(() => {
        if (layer && map?.hasLayer?.(layer)) {
          map.removeLayer(layer);
        }
      }, 0);
    },
    [mapMode, onDrawComplete]
  );

  useEffect(() => {
    let cancelled = false;
    const tryInit = () => {
      const map = leafletRef.current?.state?.map;
      if (!map || cancelled) return;
      if (!overlaysRef.current) {
        overlaysRef.current = L.featureGroup().addTo(map);
        if (!cancelled) {
          setOverlayEpoch((e) => e + 1);
        }
      }
      map.invalidateSize();
      mapReadyRef.current = true;
    };
    const id = window.setInterval(() => {
      tryInit();
      if (mapReadyRef.current) {
        window.clearInterval(id);
      }
    }, 80);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    return () => {
      const map = leafletRef.current?.state?.map;
      if (map && overlaysRef.current && map.hasLayer(overlaysRef.current)) {
        map.removeLayer(overlaysRef.current);
      }
      overlaysRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = leafletRef.current?.state?.map;
    const group = overlaysRef.current;
    if (!map || !group) return;

    group.clearLayers();

    if (borderCoords?.length && !removeMode) {
      addBorderLayers(borderCoords, group);
    }

    const sectionsInteractive = !readOnly && (removeMode || mapMode === 'sections');

    const showSectionLayer = (localKey) => {
      if (removeMode) return selectedSectionLocalIds.includes(localKey);
      if (onlySelectedOverlays) return selectedSectionLocalIds.includes(localKey);
      return mapMode === 'sections' || selectedSectionLocalIds.includes(localKey);
    };

    const showFellingFeature = (key) => {
      if (removeMode) return selectedFellingApplicationIds.includes(key);
      if (onlySelectedOverlays) return selectedFellingApplicationIds.includes(key);
      return mapMode === 'fellings' || selectedFellingApplicationIds.includes(key);
    };

    sections.forEach((sect) => {
      if (!sect.geom?.coordinates) return;
      const color =
        FellingClassesLegend[sect.felling_class]?.color ?? '#bb0000';
      const localKey = String(sect.localID);
      if (!showSectionLayer(localKey)) return;
      const selected = selectedSectionLocalIds.includes(localKey);

      const layer = L.polygon(swapCoords(sect.geom.coordinates), {
        interactive: sectionsInteractive,
        ...(selected
          ? USER_CHOICE_POLYGON
          : {
              color,
              fillColor: color,
              fillOpacity: SECTION_OPACITY,
              weight: 2,
              opacity: 1
            })
      });
      if (sectionsInteractive) {
        layer.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          onToggleSection?.(localKey);
        });
      }
      layer.addTo(group);
    });

    addFellingApplicationLayers(
      fellingApplicationsGeojson,
      group,
      selectedFellingApplicationIds,
      onToggleFellingApplication,
      !readOnly && (removeMode || mapMode === 'fellings'),
      showFellingFeature
    );

    /** Draft geometry above avdelningar / anmälningar — same style as {@link USER_CHOICE_POLYGON} (selections / ritade ytor). */
    if (hydratedAdGeometry) {
      const canClickToRemove = Boolean(removeMode && onRemoveHydratedAd && !readOnly);
      const draftOpts = {
        style: USER_CHOICE_POLYGON,
        interactive: canClickToRemove
      };
      if (canClickToRemove) {
        draftOpts.onEachFeature = (_feature, lyr) => {
          lyr.on('click', (ev) => {
            L.DomEvent.stopPropagation(ev);
            onRemoveHydratedAd();
          });
        };
      }
      const draftLayer = L.geoJSON(hydratedAdGeometry, draftOpts);
      draftLayer.addTo(group);
      draftLayer.eachLayer((lyr) => lyr.bringToFront?.());
    }

    drawnPolygons.forEach((feature, drawIndex) => {
      const gType = feature?.geometry?.type;
      if (gType === 'Polygon' || gType === 'MultiPolygon') {
        const layer = L.geoJSON(feature, {
          style: USER_CHOICE_POLYGON
        });
        layer.eachLayer((l) => {
          l.options.type = 'polygon';
          if (!readOnly && removeMode && onRemoveDrawnPolygon) {
            l.on('click', (ev) => {
              L.DomEvent.stopPropagation(ev);
              onRemoveDrawnPolygon(drawIndex);
            });
          }
          l.addTo(group);
        });
      }
    });

    const zoom = map.getZoom();
    const showPlacedName = zoom >= SELL_TIMBER_MARKER_NAME_LABEL_MIN_ZOOM;

    placedMarkers.forEach((pm, markerIdx) => {
      if (pm?.geometry?.type !== 'Point') return;
      const markerId = String(pm.properties?.sellTimberMarkerId ?? `idx-${markerIdx}`);
      const isMoveTarget = Boolean(
        markerMoveTargetId != null && String(markerMoveTargetId) === markerId
      );
      const pinIcon =
        createSellTimberPlacedMarkerLeafletIcon(pm.properties?.icon) ||
        createSellTimberPlacedMarkerLeafletIcon(DEFAULT_SELL_TIMBER_MARKER_ICON);
      const gj = L.geoJSON(pm, {
        pointToLayer: (feature, latlng) =>
          L.marker(latlng, {
            icon: pinIcon,
            opacity: isMoveTarget ? 0.92 : 1,
            riseOnHover: true
          })
      });
      gj.eachLayer((layer) => {
        if (!readOnly) {
          if (removeMode && onRemoveMarker) {
            layer.on('click', (ev) => {
              L.DomEvent.stopPropagation(ev);
              onRemoveMarker(markerId);
            });
          } else if (mapMode === 'marker' && onMarkerSelectForMove) {
            layer.on('click', (ev) => {
              L.DomEvent.stopPropagation(ev);
              onMarkerSelectForMove(markerId);
            });
          }
        }
        if (showPlacedName) {
          const raw = pm.properties?.name;
          const label = raw != null ? String(raw).trim() : '';
          if (label) {
            const latlng = layer.getLatLng?.();
            if (latlng) {
              L.marker(latlng, {
                icon: placedMarkerNameLabelDivIcon(label),
                interactive: false,
                zIndexOffset: 1000
              }).addTo(group);
            }
          }
        }
      });
      gj.addTo(group);
      gj.eachLayer((lyr) => lyr.bringToFront?.());
    });

    const bounds = group.getBounds?.();
    if (bounds?.isValid?.() && !hasDoneInitialFitRef.current) {
      map.fitBounds(bounds, { maxZoom: 17, padding: [24, 24] });
      hasDoneInitialFitRef.current = true;
    }
    map.invalidateSize();
  }, [
    mapMode,
    sections,
    borderCoords,
    fellingApplicationsGeojson,
    selectedSectionLocalIds,
    selectedFellingApplicationIds,
    drawnPolygons,
    placedMarkers,
    markerMoveTargetId,
    onToggleSection,
    onToggleFellingApplication,
    onRemoveDrawnPolygon,
    onRemoveMarker,
    onMarkerSelectForMove,
    onRemoveHydratedAd,
    overlayEpoch,
    readOnly,
    onlySelectedOverlays,
    removeMode,
    hydratedAdGeometry,
    mapZoomForLabels
  ]);

  useEffect(() => {
    const map = leafletRef.current?.state?.map;
    if (!map) return;
    const syncZoom = () => setMapZoomForLabels(map.getZoom());
    syncZoom();
    map.on('zoomend', syncZoom);
    return () => {
      map.off('zoomend', syncZoom);
    };
  }, [overlayEpoch]);

  useEffect(() => {
    const map = leafletRef.current?.state?.map;
    if (!map) return;

    if (!virkesLabelsGroupRef.current) {
      virkesLabelsGroupRef.current = L.layerGroup().addTo(map);
    }
    const labelGroup = virkesLabelsGroupRef.current;
    labelGroup.clearLayers();

    if (!readOnly) {
      return;
    }

    if (map.getZoom() < virkesLabelMinZoom) {
      return;
    }

    const features = collectFeaturesFromMapSelection(
      {
        sections,
        selectedSectionLocalIds,
        fellingApplicationsGeojson,
        selectedFellingApplicationIds,
        drawnPolygons,
        customAreaFromAd
      },
      hydratedAdGeometry
    );
    if (!features.length) {
      return;
    }

    features.forEach((feature, i) => {
      const fromTable = virkesLabels?.[i];
      const labelText =
        fromTable != null && String(fromTable).trim() !== ''
          ? String(fromTable)
          : feature.properties?.name;
      if (!labelText) return;
      const ll = latLngLabelAnchorFromGeometry(feature.geometry);
      if (!ll) return;
      L.marker(ll, {
        icon: virkesSummaryLabelDivIcon(labelText),
        interactive: false,
        zIndexOffset: 800
      }).addTo(labelGroup);
    });
  }, [
    readOnly,
    virkesLabels,
    hydratedAdGeometry,
    overlayEpoch,
    sections,
    borderCoords,
    fellingApplicationsGeojson,
    selectedSectionLocalIds,
    selectedFellingApplicationIds,
    drawnPolygons,
    customAreaFromAd,
    mapZoomForLabels,
    virkesLabelMinZoom
  ]);

  /** Step 2: avdelning localID + avverkningsanmälan id on polygons (same visibility as overlay layers). */
  useEffect(() => {
    const map = leafletRef.current?.state?.map;
    if (!map) return;

    if (!areaIdLabelsGroupRef.current) {
      areaIdLabelsGroupRef.current = L.layerGroup().addTo(map);
    }
    const labelGroup = areaIdLabelsGroupRef.current;
    labelGroup.clearLayers();

    if (!showAreaIdLabels || readOnly) {
      return;
    }

    if (map.getZoom() < MIN_ZOOM_FOR_AREA_ID_LABELS) {
      return;
    }

    const showSectionLayer = (localKey) => {
      if (removeMode) return selectedSectionLocalIds.includes(localKey);
      if (onlySelectedOverlays) return selectedSectionLocalIds.includes(localKey);
      return mapMode === 'sections' || selectedSectionLocalIds.includes(localKey);
    };

    const showFellingFeature = (key) => {
      if (removeMode) return selectedFellingApplicationIds.includes(key);
      if (onlySelectedOverlays) return selectedFellingApplicationIds.includes(key);
      return mapMode === 'fellings' || selectedFellingApplicationIds.includes(key);
    };

    sections.forEach((sect) => {
      if (!sect.geom?.coordinates) return;
      const localKey = String(sect.localID);
      if (!showSectionLayer(localKey)) return;
      const ll = latLngLabelAnchorFromGeometry(sect.geom);
      if (!ll) return;
      L.marker(ll, {
        icon: step2AreaIdLabelDivIcon(localKey),
        interactive: false,
        zIndexOffset: 750
      }).addTo(labelGroup);
    });

    const fellingFeatures =
      fellingApplicationsGeojson?.type === 'FeatureCollection'
        ? fellingApplicationsGeojson.features
        : fellingApplicationsGeojson?.type === 'Feature'
          ? [fellingApplicationsGeojson]
          : [];

    fellingFeatures.forEach((feature, index) => {
      const key = fellingApplicationKeyFromFeature(feature, index);
      if (!showFellingFeature(key)) return;
      const ll = latLngLabelAnchorFromGeometry(feature.geometry);
      if (!ll) return;
      L.marker(ll, {
        icon: step2AreaIdLabelDivIcon(key),
        interactive: false,
        zIndexOffset: 750
      }).addTo(labelGroup);
    });
  }, [
    showAreaIdLabels,
    readOnly,
    overlayEpoch,
    mapMode,
    sections,
    fellingApplicationsGeojson,
    selectedSectionLocalIds,
    selectedFellingApplicationIds,
    onlySelectedOverlays,
    removeMode,
    mapZoomForLabels
  ]);

  useEffect(() => {
    return () => {
      const map = leafletRef.current?.state?.map;
      const g = virkesLabelsGroupRef.current;
      if (map && g && map.hasLayer(g)) {
        map.removeLayer(g);
      }
      virkesLabelsGroupRef.current = null;
      const g2 = areaIdLabelsGroupRef.current;
      if (map && g2 && map.hasLayer(g2)) {
        map.removeLayer(g2);
      }
      areaIdLabelsGroupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (readOnly || removeMode || mapMode !== 'marker') {
      return;
    }
    const map = leafletRef.current?.state?.map;
    if (!map) return;

    const onMapClick = (e) => {
      if (markerMoveTargetId && onMarkerMoveTo) {
        onMarkerMoveTo(markerMoveTargetId, e.latlng);
      } else {
        onMarkerAddAt?.(e.latlng);
      }
    };

    map.on('click', onMapClick);

    return () => {
      map.off('click', onMapClick);
    };
  }, [
    mapMode,
    overlayEpoch,
    readOnly,
    removeMode,
    markerMoveTargetId,
    onMarkerAddAt,
    onMarkerMoveTo
  ]);

  /**
   * Cursor hint: Leaflet.draw’s built-in {@link L.Draw.Tooltip} (same DOM/CSS as L.Draw.Marker / polygon).
   * We do not use L.Draw.Marker here (custom multi-marker + move flow), so we instantiate the tooltip ourselves.
   * Requires `map.options.drawControlTooltips !== false` (Leaflet.draw default: true).
   * @see https://leaflet.github.io/Leaflet.draw/docs/leaflet-draw-latest.html#L.Draw.Tooltip
   */
  useEffect(() => {
    if (readOnly || removeMode || mapMode !== 'marker') {
      return;
    }
    const map = leafletRef.current?.state?.map;
    if (!map || typeof L === 'undefined' || !L.Draw?.Tooltip) {
      return;
    }

    const hintText =
      markerMoveTargetId != null
        ? 'Klicka där du vill flytta markören'
        : placedMarkers.length > 0
          ? markerHasMarkersHint ?? 'Klicka på en markör för att flytta den.'
          : markerEmptyHint ?? 'Klicka för att placera markör';

    const drawTooltip = new L.Draw.Tooltip(map);
    drawTooltip.updateContent({ text: hintText, subtext: '' });

    const initialLl = map.getCenter();
    if (initialLl) {
      drawTooltip.updatePosition(initialLl);
    }

    const onMove = (e) => {
      if (e?.latlng) drawTooltip.updatePosition(e.latlng);
    };
    map.on('mousemove', onMove);

    return () => {
      map.off('mousemove', onMove);
      drawTooltip.dispose();
    };
  }, [
    mapMode,
    overlayEpoch,
    readOnly,
    removeMode,
    placedMarkers.length,
    markerMoveTargetId,
    markerEmptyHint,
    markerHasMarkersHint
  ]);

  useEffect(() => {
    if (readOnly || removeMode || mapMode !== 'draw') {
      safeStopDraw(leafletRef);
      return;
    }
    const map = leafletRef.current?.state?.map;
    if (!map) return;

    const id = window.setTimeout(() => {
      leafletRef.current?.startDraw?.('Polygon');
    }, 50);

    return () => {
      window.clearTimeout(id);
      safeStopDraw(leafletRef);
    };
  }, [mapMode, overlayEpoch, drawnPolygons.length, readOnly, removeMode]);

  useEffect(() => {
    if (removeMode) {
      safeStopDraw(leafletRef);
    }
  }, [removeMode]);

  useEffect(() => {
    const map = leafletRef.current?.state?.map;
    const el = map?.getContainer?.();
    if (!el) return;
    if (removeMode && !readOnly) {
      el.style.cursor = 'pointer';
    } else if (!readOnly && mapMode === 'marker') {
      el.style.cursor = 'crosshair';
    } else if (!readOnly) {
      el.style.cursor = '';
    }
    return () => {
      if (el && !readOnly) el.style.cursor = '';
    };
  }, [removeMode, readOnly, overlayEpoch, mapMode]);

  useEffect(() => {
    const syncFullscreen = () => {
      const shell = mapShellRef.current;
      setMapShellFullscreen(Boolean(shell && getFullscreenElement() === shell));
      const map = leafletRef.current?.state?.map;
      if (map) {
        requestAnimationFrame(() => map.invalidateSize());
      }
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
    };
  }, []);

  const handleZoomIn = () => {
    leafletRef.current?.zoomIn?.();
  };

  const handleZoomOut = () => {
    leafletRef.current?.zoomOut?.();
  };

  const handleMapFullscreenToggle = () => {
    const shell = mapShellRef.current;
    if (!shell) return;
    if (getFullscreenElement() === shell) {
      void exitDocumentFullscreen();
    } else {
      void requestElementFullscreen(shell);
    }
  };

  const staticPath = typeof Window !== 'undefined' && Window.static_path ? Window.static_path : '';

  /** Same toolbox chrome as Karta.js (ToolboxContainer + per-button classes). */
  const mapToolBtnClass =
    'mb-1 flex items-center justify-center rounded bg-white hover-bg-supp-forest-green-300';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/*
        VBLeaflet must stay in the light DOM (same pattern as Karta.js: map is not inside TailwindWrapper).
        Leaflet uses document.getElementById for the container id; nodes inside a shadow root are not found.
      */}
      <div
        ref={(el) => {
          mapShellRef.current = el;
          setMapModalContainer(el);
        }}
        style={{
          position: 'relative',
          width: '100%',
          height: embedHeight,
          minHeight: readOnly ? 220 : 320,
          overflow: wmsIdentifyModal.open ? 'visible' : 'hidden',
          borderRadius: 8,
          border: '1px solid #e6e6e6'
        }}
      >
        <VBLeaflet
          ref={leafletRef}
          id={mapId}
          style={{ position: 'absolute', inset: 0, height: '100%', width: '100%' }}
          baseMap="Mapbox Satelite"
          mapOptions={{ keyboard: false }}
          finishDraw={finishDraw}
        />
        <div
          className="absolute flex flex-col bg-neutral-cloud shadow rounded p-1"
          style={{ right: 10, top: 10, zIndex: 1000, width: 40 }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            role="button"
            tabIndex={0}
            onClick={handleZoomIn}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleZoomIn();
              }
            }}
            className={mapToolBtnClass}
            style={{ height: '30px' }}
            title="Zooma in"
            aria-label="Zooma in"
          >
            <img src={`${staticPath}img/icons/plus.svg`} alt="" height={20} width={20} />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={handleZoomOut}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleZoomOut();
              }
            }}
            className={mapToolBtnClass}
            style={{ height: '30px' }}
            title="Zooma ut"
            aria-label="Zooma ut"
          >
            <img src={`${staticPath}img/icons/minus.svg`} alt="" height={30} width={30} />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={handleMapFullscreenToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleMapFullscreenToggle();
              }
            }}
            className="flex items-center justify-center rounded bg-white hover-bg-supp-forest-green-300"
            style={{ height: '30px' }}
            title={mapShellFullscreen ? 'Avsluta helskärm' : 'Helskärm'}
            aria-label={mapShellFullscreen ? 'Avsluta helskärm' : 'Visa kartan i helskärm'}
          >
            {mapShellFullscreen ? (
              <Icon name="ArrowsIn" size="large" className="text-charcoal-500" />
            ) : (
              <Icon name="ArrowsOut" size="large" className="text-charcoal-500" />
            )}
          </div>
        </div>
      </div>
      <SellTimberWmsIdentifyModal
        isOpen={wmsIdentifyModal.open}
        loading={wmsIdentifyModal.loading}
        sections={wmsIdentifyModal.sections}
        onClose={closeWmsIdentifyModal}
        portalContainer={mapModalContainer}
      />
    </div>
  );
}
